import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Trophy, MapPin, Clock, ChevronRight, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';

export default function LiveMatchesPage({ user, setUser }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await axios.get(`${API}/matches/live`);
        setMatches(response.data);
      } catch (error) {
        console.error('Error fetching live matches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
    // Poll every 10 seconds
    const interval = setInterval(fetchMatches, 10000);
    return () => clearInterval(interval);
  }, []);

  const formatOvers = (overs) => {
    const fullOvers = Math.floor(overs);
    const balls = Math.round((overs - fullOvers) * 10);
    return `${fullOvers}.${balls}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 main-content-mobile">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Link to="/">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <h1 className="font-bold text-lg">Live Matches</h1>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-none">
              {matches.length} Live
            </Badge>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="pt-14 px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-48 rounded-2xl"></div>)}
            </div>
          ) : matches.length > 0 ? (
            <div className="space-y-4">
              {matches.map((match) => {
                const currentInnings = match.innings?.find(i => !i.is_completed) || match.innings?.[match.innings.length - 1];
                const battingTeam = currentInnings?.batting_team_id === match.team_a_id ? match.team_a : match.team_b;
                const bowlingTeam = currentInnings?.batting_team_id === match.team_a_id ? match.team_b : match.team_a;

                return (
                  <Link key={match.match_id} to={`/live/${match.match_id}`}>
                    <Card className="card-base card-live cursor-pointer overflow-hidden">
                      <CardContent className="p-0">
                        {/* Live Header */}
                        <div className="bg-red-50 px-4 py-2 flex items-center justify-between border-b border-red-100">
                          <div className="flex items-center gap-2">
                            <div className="live-dot"></div>
                            <span className="text-red-600 font-semibold text-sm">LIVE</span>
                          </div>
                          <span className="text-slate-500 text-sm">{match.overs} overs</span>
                        </div>

                        {/* Score Display */}
                        <div className="p-4">
                          {/* Batting Team */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl team-logo-placeholder text-lg">
                                {battingTeam?.short_name?.charAt(0) || battingTeam?.name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-semibold text-lg">{battingTeam?.name}</p>
                                <p className="text-xs text-slate-500">Batting</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="score-display">
                                {currentInnings?.total_runs || 0}/{currentInnings?.total_wickets || 0}
                              </p>
                              <p className="score-overs">
                                ({formatOvers(currentInnings?.total_overs || 0)} ov)
                              </p>
                            </div>
                          </div>

                          {/* Bowling Team */}
                          <div className="flex items-center justify-between opacity-60">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg team-logo-placeholder text-sm">
                                {bowlingTeam?.short_name?.charAt(0) || bowlingTeam?.name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium">{bowlingTeam?.name}</p>
                                <p className="text-xs text-slate-500">Bowling</p>
                              </div>
                            </div>
                            {match.innings?.length > 1 && (
                              <p className="font-stats text-lg">
                                {match.innings[0].total_runs}/{match.innings[0].total_wickets}
                              </p>
                            )}
                          </div>

                          {/* Target */}
                          {currentInnings?.target && (
                            <div className="mt-4 pt-4 border-t text-center">
                              <p className="text-slate-600">
                                Need <span className="font-bold text-blue-600">{currentInnings.target - currentInnings.total_runs}</span> runs from{' '}
                                <span className="font-bold">{(match.overs * 6) - currentInnings.total_balls}</span> balls
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="bg-slate-50 px-4 py-3 flex items-center justify-between text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {match.ground}
                          </span>
                          <span className="text-blue-600 font-medium flex items-center gap-1">
                            View Full Scorecard <ChevronRight className="w-4 h-4" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="empty-state bg-white rounded-2xl mt-8">
              <div className="empty-state-icon">
                <Radio className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900">No Live Matches</h3>
              <p className="text-slate-500 text-sm mt-1">Check back during match time</p>
              <Link to="/leagues" className="mt-4 inline-block">
                <Badge className="bg-blue-600 text-white cursor-pointer">View Upcoming Matches</Badge>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
